import { FC, PropsWithChildren, ReactNode } from "react";
import styled, { css } from "styled-components";

import Text from "@/components/Text";

const Alert = styled.div<{ $type: NotificationType; $asListItem: boolean }>`
  border-radius: 4px;
  background: ${({ theme }) => theme.colors.white};
  border: 1px solid ${({ theme }) => theme.colors.grayscale};
  border-left: 5px solid ${({ theme, $type }) => theme.colors[$type]};
  padding: 18px 16px 18px 12px;
  display: flex;
  align-items: center;
  ${({ $asListItem }) =>
    $asListItem &&
    css`
      border-left: 0;
      border-bottom: 0;
    `}
`;

const Heading = styled(Text)`
  display: flex;
  align-items: center;
`;

const Icon = styled.svg<{ $type: NotificationType }>`
  color: ${({ theme, $type }) => theme.colors[$type]};
  margin-right: 8px;
  flex-shrink: 0;
`;

const Content = styled.div`
  flex: 1;
`;

const Actions = styled.div`
  display: flex;
  gap: 8px;
`;

const Description = styled(Text)`
  margin-top: 4px;
  padding-left: 26px;
  strong {
    font-weight: 700;
  }
`;

type NotificationType = "error" | "success" | "warning" | "info";

interface Props {
  actions?: ReactNode;
  heading: string;
  type?: NotificationType;
  asListItem?: boolean;
}

const Notification: FC<PropsWithChildren<Props>> = ({
  children,
  actions,
  heading,
  type = "error",
  asListItem = false,
}) => {
  return (
    <Alert $type={type} $asListItem={asListItem}>
      <Content>
        <Heading $type="smallBold">
          <Icon
            $type={type}
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect width="18" height="18" rx="9" fill="currentColor" />
            <path
              d="M8 11.5882V13H10V11.5882H8ZM8 5V10.6471H10V5H8Z"
              fill="white"
            />
          </Icon>
          {heading}
        </Heading>
        {!!children && (
          <Description $type="smallNormal" $color="gray">
            {children}
          </Description>
        )}
      </Content>
      {!!actions && <Actions>{actions}</Actions>}
    </Alert>
  );
};

export default Notification;
